import React from "react";
import { AppTheme } from "./App";

interface InputProps {
  theme: AppTheme;
  currentNames: string[];
  onShowWheel: (finalNames: string[]) => void;
  trioMode: boolean;
  onToggleTrioMode: () => void;
}

const Input: React.FC<InputProps> = (props) => {
  const [names, setNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    // Initialize names state based on props, ensuring an empty slot at the end
    const initialNames = props.currentNames.length === 0 || props.currentNames[props.currentNames.length - 1] !== "" 
                        ? [...props.currentNames, ""] 
                        : [...props.currentNames];
    setNames(initialNames);
  }, [props.currentNames]);

  const setValue = (index: number, value: string) => {
    const newNames = [...names];
    newNames[index] = value.replace("\n", "");

    // Add a new empty input field if the last one is typed into
    if (index === newNames.length - 1 && value.trim() !== "") {
      newNames.push("");
    } 
    // Remove the current field if it's empty and the next one is also empty (and it's not the last field)
    else if (index < newNames.length - 1 && value === "" && newNames[index+1] === "") {
       newNames.splice(index, 1);
    }

    setNames(newNames);
  };

  const handleKeyPress = (
    index: number,
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    switch (event.key) {
      case "Enter":
      case "ArrowDown":
        event.preventDefault();
        const nextIndex = index + 1;
        if (nextIndex < names.length) {
          document.getElementById(`textbox-${nextIndex}`)?.focus();
        }
        break;
      case "Backspace":
      case "Delete":
        if (names[index]) {
          return;
        }
        if (index === names.length - 1) {
          return;
        }
        const newNames = [...names];
        newNames.splice(index, 1);
        setNames(newNames);
        if (index > 0) {
          document.getElementById(`textbox-${index - 1}`)?.focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (index > 0) {
          document.getElementById(`textbox-${index - 1}`)?.focus();
        }
        break;
      default:
        break;
    }
  };

  const handleCreateWheel = () => {
    const createButton = document.getElementById("create-button");
    if (createButton) {
      createButton.textContent = "Skapar...";
    }
    
    const finalValidNames = names.filter((name) => name.trim() !== "");
    const finalTrimmedNames = finalValidNames.map((name) => name.trim());
    
    // Pass the current, validated names to the parent component
    props.onShowWheel(finalTrimmedNames);
  };

  const filledCount = names.filter((n) => n.trim() !== "").length;
  const canShowWheel = filledCount >= 3;

  return (
    <div className="input-container">
      <div className="settings-card">
        <div className="settings-section">
          <div className="settings-section-head">
            <h2 className="settings-section-title">Vilka ska fika?</h2>
            <span className="settings-section-hint">
              {canShowWheel
                ? `${filledCount} namn`
                : `${filledCount} av minst 3 namn`}
            </span>
          </div>
          <div className="name-input-area">
            {names.map((name, index) => {
              return (
                <textarea
                  key={`textbox-${index}`}
                  id={`textbox-${index}`}
                  className="TextRow"
                  rows={1}
                  cols={25}
                  value={name}
                  onChange={(e) => setValue(index, e.target.value)}
                  onKeyUp={(e) => handleKeyPress(index, e)}
                  placeholder="Lägg till ett namn här"
                />
              );
            })}
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section-head">
            <h2 className="settings-section-title">Läge</h2>
          </div>
          <div
            className="mode-segmented"
            role="group"
            aria-label="Välj läge"
          >
            <button
              type="button"
              className={`mode-segment ${!props.trioMode ? "active" : ""}`}
              aria-pressed={!props.trioMode}
              onClick={() => {
                if (props.trioMode) props.onToggleTrioMode();
              }}
            >
              Par
            </button>
            <button
              type="button"
              id="toggle-trio-button"
              className={`mode-segment ${props.trioMode ? "active" : ""}`}
              aria-pressed={props.trioMode}
              onClick={() => {
                if (!props.trioMode) props.onToggleTrioMode();
              }}
            >
              Trio
            </button>
          </div>
        </div>
      </div>

      <div className="button-container">
        <button
          id="create-button"
          className={`Button ${props.theme}-button show-wheel-button`}
          onClick={handleCreateWheel}
          disabled={!canShowWheel}
        >
          {canShowWheel ? "Visa Fikahjulet!" : "Minst 3 namn!"}
        </button>
      </div>
    </div>
  );
};

export default Input;
